import moment from 'moment';

export const PRODUCT_FIELDS = ['ABPB', 'BJRL', 'BJTL', 'PBS', 'RPPS', 'RPLBS', 'LBS', 'TCEJ', 'RS', 'MEP'];
export const WORK_TYPES = ['Electrical Maintenance', 'Machine Maintenance', 'New Work'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const ALL_YEARS = 'all';
export const ALL_MONTHS = 'all';
export const NO_SELECTION = '__NO_SELECTION__';

const CHART_COLORS = [
  '#4863A0', '#4cceac', '#e2726e', '#ff9800', '#9c27b0',
  '#00bcd4', '#795548', '#607d8b', '#8bc34a', '#ff5722',
  '#3f51b5', '#009688', '#f44336', '#cddc39', '#673ab7',
  '#2196f3', '#ffc107', '#e91e63', '#26a69a', '#5c6bc0',
  '#ab47bc', '#66bb6a', '#ef5350', '#42a5f5', '#ffa726',
];

export function getChartColor(i) {
  return CHART_COLORS[((i % CHART_COLORS.length) + CHART_COLORS.length) % CHART_COLORS.length];
}

export function getChartColors(count) {
  return Array.from({ length: Math.max(0, count) }, (_, i) => getChartColor(i));
}

export function normalizeYearMonth(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const formats = ['MMM-YYYY', 'MMM yyyy', 'MMM-yyyy', 'YYYY-MM', 'YYYY-MM-DD', 'DD-MM-YYYY'];
  for (const fmt of formats) {
    const m = moment(raw, fmt, true);
    if (m.isValid()) return m.format('MMM-YYYY');
  }
  const m = moment(raw);
  return m.isValid() ? m.format('MMM-YYYY') : '';
}

export function isValidPeriodKey(ym) {
  return Boolean(ym) && moment(ym, 'MMM-YYYY', true).isValid();
}

export function parseRecords(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  return Object.keys(json).map((k) => json[k]);
}

export async function fetchMisData(endpoint) {
  const res = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}${endpoint}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
  return parseRecords(await res.json());
}

export function isOpenRecord(r) {
  return !r?.CompleteDate;
}

export function sumProductFields(row) {
  return PRODUCT_FIELDS.reduce((s, f) => {
    const v = Number(row?.[f]);
    return Number.isFinite(v) ? s + v : s;
  }, 0);
}

export function percentageIncrease(current, previous) {
  const prev = Number.isFinite(previous) ? previous : 0;
  const curr = Number.isFinite(current) ? current : 0;
  if (prev === 0) return curr > 0 ? 100 : 0;
  if (curr > prev) return ((curr - prev) / prev) * 100;
  return 0;
}

export function filterMaintenance(records, year, month) {
  return records.filter((r) => {
    const d = moment(r.AssignDate);
    if (!d.isValid()) return false;
    if (year !== ALL_YEARS && d.year() !== Number(year)) return false;
    if (month !== ALL_MONTHS && d.month() !== Number(month)) return false;
    return true;
  });
}

export function filterYearMonthRecords(records, year, month) {
  return records.filter((r) => {
    const m = moment(normalizeYearMonth(r.YearMonth), 'MMM-YYYY', true);
    if (!m.isValid()) return false;
    if (year !== ALL_YEARS && m.year() !== Number(year)) return false;
    if (month !== ALL_MONTHS && m.month() !== Number(month)) return false;
    return true;
  });
}

export function getYearsFromMaintenance(records) {
  const years = new Set();
  records.forEach((r) => {
    const d = moment(r.AssignDate);
    if (d.isValid()) years.add(d.year());
  });
  if (!years.size) years.add(moment().year());
  return Array.from(years).sort((a, b) => b - a);
}

export function getYearsFromYearMonth(records) {
  const years = new Set();
  records.forEach((r) => {
    const m = moment(normalizeYearMonth(r.YearMonth), 'MMM-YYYY', true);
    if (m.isValid()) years.add(m.year());
  });
  if (!years.size) years.add(moment().year());
  return Array.from(years).sort((a, b) => b - a);
}

export function getPeriodLabel(years, periods) {
  if (isNoSelection(years) || isNoSelection(periods)) return 'None selected';
  const yearArr = toFilterArray(years, ALL_YEARS);
  const periodArr = toFilterArray(periods, null);

  const formatYears = () => {
    if (!yearArr.length) return 'All Years';
    if (yearArr.length === 1) return yearArr[0];
    return `${yearArr.length} years`;
  };
  const formatPeriods = () => {
    if (!periodArr.length) return 'All Periods';
    if (periodArr.length === 1) return periodArr[0];
    return `${periodArr.length} periods`;
  };

  if (!yearArr.length && !periodArr.length) return 'All Time';
  if (!yearArr.length) return formatPeriods();
  if (!periodArr.length) return formatYears();
  return `${formatPeriods()} · ${formatYears()}`;
}

export function groupByWorkType(records) {
  return records.reduce((acc, curr) => {
    const wt = curr.WorkType || 'Unknown';
    const idx = acc.findIndex((x) => x.Department === wt);
    if (idx === -1) {
      acc.push({ Department: wt, openCount: isOpenRecord(curr) ? 1 : 0, closeCount: isOpenRecord(curr) ? 0 : 1 });
    } else if (isOpenRecord(curr)) acc[idx].openCount += 1;
    else acc[idx].closeCount += 1;
    return acc;
  }, []);
}

export function groupByAssignee(records) {
  return records.reduce((acc, curr) => {
    const key = `${curr.AssignedTo || 'Unassigned'}-${curr.Department || ''}`;
    const idx = acc.findIndex((x) => x.AssignedTo === key);
    if (idx === -1) {
      acc.push({ AssignedTo: key, Assignee: curr.AssignedTo || 'Unassigned', Department: curr.Department || '', count: 1 });
    } else acc[idx].count += 1;
    return acc;
  }, []);
}

export function buildWorkTypeMonthlyData(records, year) {
  const y = year === ALL_YEARS ? moment().year() : Number(year);
  return {
    labels: MONTHS,
    datasets: WORK_TYPES.map((wt, i) => ({
      label: wt,
      data: MONTHS.map((mn) =>
        records.filter((r) => {
          const d = moment(r.AssignDate);
          return r.WorkType === wt && d.isValid() && d.format('MMM') === mn && d.year() === y;
        }).length
      ),
      backgroundColor: getChartColor(i),
      borderRadius: 4,
    })),
  };
}

export function getMonthLabelsFromRecords(records, dateField = 'AssignDate', maxMonths = 12) {
  const map = new Map();
  records.forEach((r) => {
    const d = moment(r[dateField]);
    if (!d.isValid()) return;
    const key = d.format('MMM-YY');
    if (!map.has(key)) map.set(key, d.clone().startOf('month'));
  });
  return [...map.entries()]
    .sort((a, b) => a[1].valueOf() - b[1].valueOf())
    .map(([key]) => key)
    .slice(-maxMonths);
}

export function getMachineTaskItems(records) {
  return records.filter((r) => r.Machine);
}

export function buildDeptOpenClosed(records) {
  const depts = [...new Set(records.map((r) => r.Department).filter(Boolean))];
  return {
    labels: depts,
    datasets: [
      { label: 'Open', data: depts.map((d) => records.filter((r) => r.Department === d && isOpenRecord(r)).length), backgroundColor: getChartColors(depts.length), borderRadius: 4 },
      { label: 'Closed', data: depts.map((d) => records.filter((r) => r.Department === d && !isOpenRecord(r)).length), backgroundColor: getChartColors(depts.length).map((_, i) => getChartColor(i + depts.length)), borderRadius: 4 },
    ],
  };
}

export function buildMachinePie(records) {
  const items = getMachineTaskItems(records);
  const machines = [...new Set(items.map((r) => r.Machine))];
  return {
    labels: machines,
    datasets: [{ data: machines.map((m) => items.filter((r) => r.Machine === m).length), backgroundColor: machines.map((_, i) => getChartColor(i)) }],
  };
}

export function buildMachineLine(records, year) {
  const y = year === ALL_YEARS ? moment().year() : Number(year);
  const items = getMachineTaskItems(records);
  const machines = [...new Set(items.map((r) => r.Machine))].slice(0, 8);
  return {
    labels: MONTHS,
    datasets: machines.map((machine, i) => ({
      label: machine,
      data: MONTHS.map((mn) =>
        items.filter((r) => {
          const d = moment(r.AssignDate);
          return r.Machine === machine && d.isValid() && d.format('MMM') === mn && d.year() === y;
        }).length
      ),
      borderColor: getChartColor(i),
      backgroundColor: getChartColor(i),
      tension: 0.3,
    })),
  };
}

export function buildMonthlyTrend(records) {
  const map = {};
  records.forEach((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (ym) map[ym] = (map[ym] || 0) + sumProductFields(r);
  });
  const labels = Object.keys(map).sort((a, b) => moment(a, 'MMM-YYYY').valueOf() - moment(b, 'MMM-YYYY').valueOf());
  return { labels, data: labels.map((k) => map[k]) };
}

export function buildProductComparison(supply, warranty) {
  const st = {};
  const wt = {};
  supply.forEach((r) => PRODUCT_FIELDS.forEach((f) => { const v = Number(r[f]); if (Number.isFinite(v)) st[f] = (st[f] || 0) + v; }));
  warranty.forEach((r) => PRODUCT_FIELDS.forEach((f) => { const v = Number(r[f]); if (Number.isFinite(v)) wt[f] = (wt[f] || 0) + v; }));
  return {
    labels: PRODUCT_FIELDS,
    datasets: [
      { label: 'Supply', data: PRODUCT_FIELDS.map((f) => st[f] || 0), backgroundColor: getChartColors(PRODUCT_FIELDS.length), borderRadius: 4 },
      { label: 'Warranty', data: PRODUCT_FIELDS.map((f) => wt[f] || 0), backgroundColor: getChartColors(PRODUCT_FIELDS.length).map((_, i) => getChartColor(i + PRODUCT_FIELDS.length)), borderRadius: 4 },
    ],
  };
}

export function buildRejectionTrend(records) {
  const map = {};
  records.forEach((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (!ym) return;
    let t = 0; let c = 0;
    PRODUCT_FIELDS.forEach((f) => { const v = parseFloat(r[f]); if (Number.isFinite(v)) { t += v; c++; } });
    if (c) map[ym] = t / c;
  });
  const labels = Object.keys(map).sort((a, b) => moment(a, 'MMM-YYYY').valueOf() - moment(b, 'MMM-YYYY').valueOf());
  return { labels, data: labels.map((k) => Number(map[k].toFixed(2))) };
}

export const ALL_PRODUCTS = 'all';
export const ALL_DEPARTMENTS = 'all';

export function getDefaultDashboardYear() {
  return String(moment().year());
}

export function getDefaultDashboardMonth() {
  return String(moment().month());
}

export function getDefaultDashboardPeriod() {
  return moment().format('MMM-YY');
}

export function getDefaultFiscalYear() {
  return getFiscalYearLabel(moment());
}

export function getDashboardDefaultFilters(type = 'warranty') {
  const periods = [getDefaultDashboardPeriod()];
  if (type === 'business') {
    return { years: [getDefaultFiscalYear()], periods: [], products: [], departments: [] };
  }
  if (type === 'maintenance') {
    return { years: [getDefaultDashboardYear()], periods, departments: [] };
  }
  return { years: [getDefaultDashboardYear()], periods, products: [] };
}

export function getFiscalYearLabel(date) {
  const m = moment(date);
  if (!m.isValid()) return getDefaultFiscalYear();
  const startYear = m.month() >= 3 ? m.year() : m.year() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
}

export function parseFiscalYearStart(fiscalYearKey) {
  if (!fiscalYearKey || fiscalYearKey === ALL_YEARS) return null;
  const start = parseInt(String(fiscalYearKey).split('-')[0], 10);
  return Number.isFinite(start) ? start : null;
}

export function isDateInFiscalYear(date, fiscalYearKey) {
  const startYear = parseFiscalYearStart(fiscalYearKey);
  if (startYear === null) return true;
  const m = moment(date);
  if (!m.isValid()) return false;
  const start = moment({ year: startYear, month: 3, day: 1 });
  const end = moment({ year: startYear + 1, month: 2, day: 31 }).endOf('day');
  return m.isBetween(start, end, 'day', '[]');
}

export function periodToYearMonth(period) {
  const m = moment(period, 'MMM-YY', true);
  if (!m.isValid()) {
    return { year: getDefaultDashboardYear(), month: getDefaultDashboardMonth() };
  }
  return { year: String(m.year()), month: String(m.month()) };
}

function sortPeriodsDesc(periodSet) {
  return Array.from(periodSet).sort((a, b) => moment(b, 'MMM-YY', true).valueOf() - moment(a, 'MMM-YY', true).valueOf());
}

export function collectPeriodKeysFromMaintenance(records) {
  const set = new Set();
  records.forEach((r) => {
    const d = moment(r.AssignDate);
    if (d.isValid()) set.add(d.format('MMM-YY'));
  });
  if (!set.size) set.add(getDefaultDashboardPeriod());
  return sortPeriodsDesc(set);
}

export function collectPeriodKeysFromYearMonth(records) {
  const set = new Set();
  records.forEach((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (isValidPeriodKey(ym)) set.add(moment(ym, 'MMM-YYYY', true).format('MMM-YY'));
  });
  if (!set.size) set.add(getDefaultDashboardPeriod());
  return sortPeriodsDesc(set);
}

export function collectPeriodKeys(...recordSets) {
  const set = new Set();
  recordSets.flat().forEach((r) => {
    const d = moment(r.AssignDate);
    if (d.isValid()) { set.add(d.format('MMM-YY')); return; }
    const ym = normalizeYearMonth(r.YearMonth);
    if (isValidPeriodKey(ym)) set.add(moment(ym, 'MMM-YYYY', true).format('MMM-YY'));
  });
  if (!set.size) set.add(getDefaultDashboardPeriod());
  return sortPeriodsDesc(set);
}

export function getFiscalYearsFromData(...recordSets) {
  const years = new Set();
  recordSets.flat().forEach((r) => {
    const d = moment(r.AssignDate);
    if (d.isValid()) { years.add(getFiscalYearLabel(d)); return; }
    const ym = moment(normalizeYearMonth(r.YearMonth), 'MMM-YYYY', true);
    if (ym.isValid()) years.add(getFiscalYearLabel(ym));
  });
  if (!years.size) years.add(getDefaultFiscalYear());
  return Array.from(years).sort((a, b) => parseFiscalYearStart(b) - parseFiscalYearStart(a));
}

export function filterPeriodsByYear(periods, year, yearMode = 'calendar') {
  if (!periods?.length) return [getDefaultDashboardPeriod()];
  const years = toFilterArray(year, ALL_YEARS);
  if (!years.length) return periods;
  const merged = new Set();
  years.forEach((y) => {
    if (yearMode === 'fiscal') {
      const start = parseFiscalYearStart(y);
      if (start === null) { periods.forEach((p) => merged.add(p)); return; }
      const startM = moment({ year: start, month: 3, day: 1 });
      const endM = moment({ year: start + 1, month: 2, day: 31 });
      periods.forEach((p) => {
        const m = moment(p, 'MMM-YY', true);
        if (m.isValid() && m.isBetween(startM, endM, 'month', '[]')) merged.add(p);
      });
    } else {
      periods.forEach((p) => {
        const m = moment(p, 'MMM-YY', true);
        if (m.isValid() && m.year() === Number(y)) merged.add(p);
      });
    }
  });
  const result = sortPeriodsDesc(merged);
  return result.length ? result : periods;
}

export function toFilterArray(value, allToken) {
  if (isNoSelection(value)) return [];
  if (value === allToken || value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.filter((v) => v && v !== allToken && v !== NO_SELECTION);
  return [value];
}

export function isNoSelection(value) {
  return Array.isArray(value) && value.length === 1 && value[0] === NO_SELECTION;
}

export function isAllSelection(value, allToken) {
  return !isNoSelection(value) && toFilterArray(value, allToken).length === 0;
}

export function getProductFields(product) {
  if (isNoSelection(product)) return [];
  const fields = toFilterArray(product, ALL_PRODUCTS);
  return fields.length ? fields : PRODUCT_FIELDS;
}

export function getPrimaryPeriod(periods) {
  const arr = toFilterArray(periods, null);
  if (!arr.length) return getDefaultDashboardPeriod();
  return [...arr].sort((a, b) => moment(b, 'MMM-YY', true).valueOf() - moment(a, 'MMM-YY', true).valueOf())[0];
}

export function filterMaintenanceDashboard(records, year, period, yearMode = 'calendar') {
  if (isNoSelection(year) || isNoSelection(period)) return [];
  const years = toFilterArray(year, ALL_YEARS);
  const periods = toFilterArray(period, null);
  return records.filter((r) => {
    const d = moment(r.AssignDate);
    if (!d.isValid()) return false;
    const periodKey = d.format('MMM-YY');
    if (periods.length && !periods.includes(periodKey)) return false;
    if (years.length) {
      if (yearMode === 'fiscal') {
        if (!years.some((y) => isDateInFiscalYear(d, y))) return false;
      } else if (!years.some((y) => d.year() === Number(y))) {
        return false;
      }
    }
    return true;
  });
}

export function filterYearMonthDashboard(records, year, period, yearMode = 'calendar') {
  if (isNoSelection(year) || isNoSelection(period)) return [];
  const years = toFilterArray(year, ALL_YEARS);
  const periods = toFilterArray(period, null);
  return records.filter((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (!isValidPeriodKey(ym)) return false;
    const m = moment(ym, 'MMM-YYYY', true);
    const periodKey = m.format('MMM-YY');
    if (periods.length && !periods.includes(periodKey)) return false;
    if (years.length) {
      if (yearMode === 'fiscal') {
        if (!years.some((y) => isDateInFiscalYear(m, y))) return false;
      } else if (!years.some((y) => m.year() === Number(y))) {
        return false;
      }
    }
    return true;
  });
}

export function filterYearMonthRecordsMulti(records, years) {
  if (isNoSelection(years)) return [];
  const yearArr = toFilterArray(years, ALL_YEARS);
  if (!yearArr.length) return records;
  return records.filter((r) => {
    const m = moment(normalizeYearMonth(r.YearMonth), 'MMM-YYYY', true);
    return m.isValid() && yearArr.includes(String(m.year()));
  });
}

export const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { y: { ticks: { precision: 0, beginAtZero: true } } } };

export const machineMonthlyClusterChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { boxWidth: 12, padding: 8, font: { size: 10 } } },
    tooltip: {
      callbacks: {
        title: (items) => `Month: ${items[0]?.label ?? ''}`,
        label: (ctx) => {
          const machine = ctx.dataset.label ?? '';
          const total = ctx.parsed.y ?? 0;
          return `Machine: ${machine} | Total Task: ${total}`;
        },
      },
    },
  },
  scales: {
    x: {
      title: { display: true, text: 'Month', font: { size: 11 } },
      ticks: { maxRotation: 45, minRotation: 0, autoSkip: true, font: { size: 10 } },
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Total Task', font: { size: 11 } },
      ticks: { precision: 0, maxTicksLimit: 8 },
    },
  },
};

export const machineTotalCompletedChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' },
    tooltip: {
      callbacks: {
        title: (items) => `Machine: ${items[0]?.label ?? ''}`,
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? 0}`,
      },
    },
  },
  scales: {
    x: {
      title: { display: true, text: 'Machine', font: { size: 11 } },
      ticks: { autoSkip: false, maxRotation: 45, minRotation: 0, font: { size: 10 } },
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Total Task', font: { size: 11 } },
      ticks: { precision: 0 },
    },
  },
};

export const dailyTrendChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: (items) => `Day: ${items[0]?.label ?? ''}`,
        label: (ctx) => `Total Task: ${ctx.parsed.y ?? 0}`,
      },
    },
  },
  layout: { padding: { top: 8, bottom: 4, left: 4, right: 8 } },
  scales: {
    x: {
      title: { display: true, text: 'Day', font: { size: 10 } },
      ticks: {
        autoSkip: true,
        maxTicksLimit: 12,
        maxRotation: 45,
        minRotation: 0,
        font: { size: 9 },
      },
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Total Task', font: { size: 10 } },
      ticks: { precision: 0, maxTicksLimit: 6, font: { size: 9 } },
    },
  },
};

export const deptWorkTypeStackedChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, padding: 10, font: { size: 10 } },
    },
    tooltip: {
      callbacks: {
        title: (items) => `Department: ${items[0]?.label ?? ''}`,
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? 0}`,
      },
    },
  },
  layout: { padding: { top: 4, bottom: 4, left: 2, right: 8 } },
  scales: {
    x: {
      stacked: true,
      title: { display: true, text: 'Department', font: { size: 10 } },
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 0,
        font: { size: 9 },
        callback(value) {
          const label = this.getLabelForValue(value);
          if (!label) return '';
          return label.length > 10 ? `${label.slice(0, 9)}…` : label;
        },
      },
      grid: { display: false },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      title: { display: true, text: 'Total Task', font: { size: 10 } },
      ticks: { precision: 0, maxTicksLimit: 6, font: { size: 9 } },
    },
  },
};

export const completedOpenWorkTypeChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, padding: 10, font: { size: 10 } },
    },
    tooltip: {
      callbacks: {
        title: (items) => `Work Type: ${items[0]?.label ?? ''}`,
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? 0}`,
      },
    },
  },
  layout: { padding: { top: 4, bottom: 4, left: 2, right: 8 } },
  scales: {
    x: {
      title: { display: true, text: 'Work Type', font: { size: 10 } },
      ticks: {
        autoSkip: false,
        maxRotation: 35,
        minRotation: 0,
        font: { size: 9 },
        callback(value) {
          const label = this.getLabelForValue(value);
          if (!label) return '';
          return label.length > 12 ? `${label.slice(0, 11)}…` : label;
        },
      },
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Total Task', font: { size: 10 } },
      ticks: { precision: 0, maxTicksLimit: 6, font: { size: 9 } },
    },
  },
};
export const productBarChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  layout: { padding: { bottom: 8, left: 4, right: 8 } },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0, maxTicksLimit: 6 } },
    x: {
      ticks: { autoSkip: false, maxRotation: 45, minRotation: 45, font: { size: 10 } },
      grid: { display: false },
    },
  },
};
export const productLineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  layout: { padding: { bottom: 8, left: 4, right: 8 } },
  scales: {
    y: { beginAtZero: true, ticks: { maxTicksLimit: 6 }, title: { display: true, text: '%', font: { size: 10 } } },
    x: {
      ticks: { autoSkip: false, maxRotation: 45, minRotation: 45, font: { size: 10 } },
      grid: { display: false },
    },
  },
};
export const lineOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true } } };
export const hBarOptions = { ...barOptions, indexAxis: 'y' };

export function formatCompact(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}K`;
  return String(Math.round(v));
}

export function formatPercent(n, digits = 2) {
  const v = Number(n);
  return Number.isFinite(v) ? `${v.toFixed(digits)}%` : '0%';
}

export function sumByProduct(records, product = ALL_PRODUCTS) {
  const fields = getProductFields(product);
  return records.reduce((s, r) => {
    return s + fields.reduce((fs, f) => {
      const v = Number(r?.[f]);
      return Number.isFinite(v) ? fs + v : fs;
    }, 0);
  }, 0);
}

export function calcRejectionRate(supplyTotal, warrantyTotal) {
  if (!supplyTotal) return 0;
  return (warrantyTotal / supplyTotal) * 100;
}

export function avgRejectionFromRecords(records, product = ALL_PRODUCTS) {
  const fields = getProductFields(product);
  let sum = 0;
  let count = 0;
  records.forEach((r) => {
    fields.forEach((f) => {
      const v = parseFloat(r[f]);
      if (Number.isFinite(v)) { sum += v; count++; }
    });
  });
  return count ? sum / count : 0;
}

export function getComparisonPeriod(years, periods) {
  const primary = getPrimaryPeriod(periods);
  const { year: py, month: pm } = periodToYearMonth(primary);
  const current = moment().year(Number(py)).month(Number(pm));
  const previous = current.clone().subtract(1, 'month');
  return {
    currentYear: String(current.year()),
    currentMonth: String(current.month()),
    prevYear: String(previous.year()),
    prevMonth: String(previous.month()),
    currentLabel: current.format('MMM-YY'),
    prevLabel: previous.format('MMM-YY'),
  };
}

export function buildKpiComparison(current, previous) {
  const curr = Number.isFinite(current) ? current : 0;
  const prev = Number.isFinite(previous) ? previous : 0;
  let pct = 0;
  if (prev === 0) pct = curr > 0 ? 100 : 0;
  else pct = ((curr - prev) / prev) * 100;
  return { current: curr, previous: prev, pct: Number(pct.toFixed(0)), increased: curr >= prev };
}

export function filterMaintenanceByDept(records, department) {
  if (isNoSelection(department)) return [];
  const depts = toFilterArray(department, ALL_DEPARTMENTS);
  if (!depts.length) return records;
  return records.filter((r) => depts.includes(r.Department));
}

export function getDepartmentsFromMaintenance(records) {
  return [...new Set(records.map((r) => r.Department).filter(Boolean))].sort();
}

export function getBreakdownList(records, department) {
  let items = records.filter((r) => isOpenRecord(r) && r.WorkType === 'Machine Maintenance');
  const depts = toFilterArray(department, ALL_DEPARTMENTS);
  if (depts.length) items = items.filter((r) => depts.includes(r.Department));
  return items.map((r) => ({
    section: r.Department || '—',
    machine: r.Machine || '—',
    startDate: r.StartDate || r.AssignDate || '',
    ageing: moment(r.StartDate || r.AssignDate).isValid()
      ? moment().diff(moment(r.StartDate || r.AssignDate), 'days') : 0,
  })).sort((a, b) => b.ageing - a.ageing).slice(0, 12);
}

export function buildProductTotals(records, product = ALL_PRODUCTS) {
  const totals = {};
  const fields = getProductFields(product);
  records.forEach((r) => {
    fields.forEach((f) => {
      const v = Number(r[f]);
      if (Number.isFinite(v)) totals[f] = (totals[f] || 0) + v;
    });
  });
  return totals;
}

export function buildProductSupplyBar(records, product = ALL_PRODUCTS) {
  const totals = buildProductTotals(records, product);
  const labels = getProductFields(product);
  return {
    labels,
    datasets: [{ label: 'Supply', data: labels.map((f) => totals[f] || 0), backgroundColor: getChartColors(labels.length), borderRadius: 4 }],
  };
}

export function buildProductWarrantyBar(records, product = ALL_PRODUCTS) {
  const totals = buildProductTotals(records, product);
  const labels = getProductFields(product);
  return {
    labels,
    datasets: [{ label: 'Warranty', data: labels.map((f) => totals[f] || 0), backgroundColor: getChartColors(labels.length), borderRadius: 4 }],
  };
}

export function buildProductRejectionLine(supply, warranty, product = ALL_PRODUCTS) {
  const labels = getProductFields(product);
  const colors = getChartColors(labels.length);
  return {
    labels,
    datasets: [{
      label: 'Rejection Rate %',
      data: labels.map((f) => {
        const st = supply.reduce((a, r) => a + (Number(r[f]) || 0), 0);
        const wt = warranty.reduce((a, r) => a + (Number(r[f]) || 0), 0);
        return calcRejectionRate(st, wt);
      }),
      borderColor: '#4863A0',
      backgroundColor: colors,
      pointBackgroundColor: colors,
      pointBorderColor: colors,
      tension: 0.3,
      fill: false,
    }],
  };
}

export function buildTop5RejectionBar(supply, warranty, product = ALL_PRODUCTS) {
  const labels = getProductFields(product);
  const rates = labels.map((f) => {
    const st = supply.reduce((a, r) => a + (Number(r[f]) || 0), 0);
    const wt = warranty.reduce((a, r) => a + (Number(r[f]) || 0), 0);
    return { label: f, rate: calcRejectionRate(st, wt) };
  }).sort((a, b) => b.rate - a.rate).slice(0, 5);
  return {
    labels: rates.map((r) => r.label),
    datasets: [{ label: 'Rejection Rate %', data: rates.map((r) => Number(r.rate.toFixed(2))), backgroundColor: getChartColors(rates.length), borderRadius: 4 }],
  };
}

function monthlyTotals(records, product = ALL_PRODUCTS) {
  const map = {};
  records.forEach((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (!ym) return;
    map[ym] = (map[ym] || 0) + sumByProduct([r], product);
  });
  const sorted = Object.keys(map).sort(
    (a, b) => moment(a, 'MMM-YYYY', true).valueOf() - moment(b, 'MMM-YYYY', true).valueOf(),
  );
  const labels = sorted.map((ym) => moment(ym, 'MMM-YYYY', true).format('MMM-YY'));
  return { labels, data: sorted.map((k) => map[k]) };
}

function growthPct(data) {
  return data.map((v, i) => {
    if (i === 0 || !data[i - 1]) return 0;
    return Number((((v - data[i - 1]) / data[i - 1]) * 100).toFixed(1));
  });
}

export function buildSupplyGrowthCombo(records, product = ALL_PRODUCTS) {
  const { labels, data } = monthlyTotals(records, product);
  return {
    labels,
    datasets: [
      { type: 'bar', label: 'Supply Quantity', data, backgroundColor: '#4cceac', borderRadius: 4, yAxisID: 'y' },
      { type: 'line', label: 'Growth %', data: growthPct(data), borderColor: '#4863A0', tension: 0.3, yAxisID: 'y1' },
    ],
  };
}

export function buildWarrantyGrowthCombo(supply, warranty, product = ALL_PRODUCTS) {
  const w = monthlyTotals(warranty, product);
  const rates = w.labels.map((ym) => {
    const s = supply.filter((r) => normalizeYearMonth(r.YearMonth) === ym);
    const wr = warranty.filter((r) => normalizeYearMonth(r.YearMonth) === ym);
    return calcRejectionRate(sumByProduct(s, product), sumByProduct(wr, product));
  });
  return {
    labels: w.labels,
    datasets: [
      { type: 'bar', label: 'Warranty Quantity', data: w.data, backgroundColor: '#e2726e', borderRadius: 4, yAxisID: 'y' },
      { type: 'line', label: 'Rejection %', data: rates.map((v) => Number(v.toFixed(2))), borderColor: '#4863A0', tension: 0.3, yAxisID: 'y1' },
    ],
  };
}

export function buildRejectionRateByProductPeriod(rejection, product = ALL_PRODUCTS) {
  const fields = getProductFields(product);
  const months = {};
  rejection.forEach((r) => {
    const ym = normalizeYearMonth(r.YearMonth);
    if (!isValidPeriodKey(ym)) return;
    if (!months[ym]) months[ym] = {};
    fields.forEach((f) => {
      const v = parseFloat(r[f]);
      if (Number.isFinite(v)) months[ym][f] = v;
    });
  });
  let labelKeys = Object.keys(months).sort(
    (a, b) => moment(a, 'MMM-YYYY', true).valueOf() - moment(b, 'MMM-YYYY', true).valueOf(),
  );
  if (labelKeys.length > 12) labelKeys = labelKeys.slice(-12);
  const labels = labelKeys.map((ym) => moment(ym, 'MMM-YYYY', true).format('MMM-YY'));
  return {
    labels,
    datasets: fields.map((f, i) => ({
      label: f,
      data: labelKeys.map((ym) => months[ym]?.[f] ?? 0),
      backgroundColor: getChartColor(i),
      borderColor: getChartColor(i),
      borderRadius: 2,
      maxBarThickness: 14,
    })),
  };
}

export const rejectionPeriodChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, padding: 6, font: { size: 10 } },
    },
    tooltip: {
      callbacks: {
        title: (items) => `Month: ${items[0]?.label ?? ''}`,
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? 0}%`,
      },
    },
  },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: 'Rejection Rate %' }, ticks: { maxTicksLimit: 8 } },
    x: { title: { display: true, text: 'Month' }, ticks: { maxRotation: 45, minRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
  },
};

export function buildWorkTypePie(records) {
  const groups = groupByWorkType(records);
  return {
    labels: groups.map((g) => g.Department),
    datasets: [{
      data: groups.map((g) => g.openCount + g.closeCount),
      backgroundColor: groups.map((_, i) => getChartColor(i)),
    }],
  };
}

export function buildDeptWorkTypeStacked(records) {
  const depts = [...new Set(records.map((r) => r.Department).filter(Boolean))];
  return {
    labels: depts,
    datasets: WORK_TYPES.map((wt, i) => ({
      label: wt,
      data: depts.map((d) => records.filter((r) => r.Department === d && r.WorkType === wt).length),
      backgroundColor: getChartColor(i),
      borderRadius: 2,
    })),
  };
}

export function buildDailyTrend(records, year, month) {
  const filtered = filterMaintenance(records, year, month);
  const days = {};
  filtered.forEach((r) => {
    const d = moment(r.AssignDate);
    if (!d.isValid()) return;
    const key = d.format('DD-MM');
    days[key] = (days[key] || 0) + 1;
  });
  const labels = Object.keys(days).sort((a, b) => {
    const [da, ma] = a.split('-').map(Number);
    const [db, mb] = b.split('-').map(Number);
    return ma !== mb ? ma - mb : da - db;
  });
  return {
    labels,
    datasets: [{ label: 'Total Task', data: labels.map((k) => days[k]), borderColor: '#db4f4a', backgroundColor: 'rgba(219, 79, 74, 0.15)', tension: 0.3, fill: true }],
  };
}

export function buildCompletedOpenByWorkType(records) {
  const groups = groupByWorkType(records);
  return {
    labels: groups.map((g) => g.Department),
    datasets: [
      { label: 'Open', data: groups.map((g) => g.openCount), backgroundColor: getChartColors(groups.length), borderRadius: 4 },
      { label: 'Closed', data: groups.map((g) => g.closeCount), backgroundColor: getChartColors(groups.length).map((_, i) => getChartColor(i + groups.length)), borderRadius: 4 },
    ],
  };
}

export function buildOpenByDeptDonut(records) {
  const openRecords = records.filter(isOpenRecord);
  const depts = [...new Set(openRecords.map((r) => r.Department).filter(Boolean))];
  return {
    labels: depts,
    datasets: [{
      data: depts.map((d) => openRecords.filter((r) => r.Department === d).length),
      backgroundColor: depts.map((_, i) => getChartColor(i)),
    }],
  };
}

export function buildMachineWiseTotalCompleted(records) {
  const items = getMachineTaskItems(records);
  const machines = [...new Set(items.map((r) => r.Machine))].sort().slice(0, 12);
  return {
    labels: machines,
    datasets: [
      { label: 'Total', data: machines.map((m) => items.filter((r) => r.Machine === m).length), backgroundColor: '#4863A0', borderRadius: 2 },
      { label: 'Completed', data: machines.map((m) => items.filter((r) => r.Machine === m && !isOpenRecord(r)).length), backgroundColor: '#4cceac', borderRadius: 2 },
    ],
  };
}

export function buildMachineMonthlyCluster(records) {
  const items = getMachineTaskItems(records);
  if (!items.length) return { labels: [], datasets: [] };

  const labels = getMonthLabelsFromRecords(items);
  const machines = [...new Set(items.map((r) => r.Machine))].sort().slice(0, 12);

  return {
    labels,
    datasets: machines.map((machine, i) => ({
      label: machine,
      data: labels.map((periodKey) =>
        items.filter((r) => {
          const d = moment(r.AssignDate);
          return r.Machine === machine && d.isValid() && d.format('MMM-YY') === periodKey;
        }).length
      ),
      backgroundColor: getChartColor(i),
      borderRadius: 2,
    })),
  };
}

export const comboOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'top' } },
  scales: {
    y: { beginAtZero: true, position: 'left' },
    y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false } },
  },
};
